import { jsx as _jsx } from "react/jsx-runtime";
/**
 * S3 (Option A): single-chart bottom content đi qua 1 host duy nhất.
 *
 * Public interface giữ nguyên byte-identical (props, BottomTab, tier/paper/pine
 * behaviors) nên `VNInvestCharts.tsx` (deny-list) KHÔNG phải sửa. Bên trong
 * delegate sang `BottomPanelHost` — nơi duy nhất owns content:
 * - tier: `tierGated.*` → 2 boolean grant (fail-closed như cũ).
 * - paper_trade: render `LazyConnectedTradingPanel`, close → setActiveBottomTab(null).
 * - pine add: logic dedupe/pane-cap/routing giữ verbatim, chỉ đổi chỗ ở.
 * - pine bars fallback: `pineCompileBars` (columnar, không time) đi qua
 *   `pineEditorLegacyBars` — PineEditorPanel chỉ đọc khi `pineBase` vắng,
 *   nên 2 surface giữ đúng hành vi cũ từng có.
 */
import { lazy, Suspense, useCallback } from "react";
import { PANE_MAX_VISIBLE } from "../../../lib/core";
import { BottomPanelHost } from "../BottomPanelHost";
// BL-15: panel chỉ single-chart chạm tới giữ lazy-chunk riêng.
const LazyConnectedTradingPanel = lazy(() => import("../../trading/panel/ConnectedTradingPanel").then((m) => ({ default: m.ConnectedTradingPanel })));
export function ForkBottomPanel({ activeBottomTab, setActiveBottomTab, bottomPanelHeight, tierGated, dataResult, activeIndicatorConfigs, pineCompileBars, paneState, pushToast, t, }) {
    // Verbatim từ block cũ: dedupe → pane-cap → routing (đã khóa bởi
    // ForkBottomPanel.test.tsx: duplicate/paneLimit/addPane/addSeries).
    const handleAddIndicator = useCallback((config) => {
        // 1.6b/dedupe: re-compile cùng series không clone line + không
        // duplicate-key trong legend.
        const existing = paneState.panes.some((p) => (p.series ?? []).some((s) => s.type === config?.type));
        if (existing) {
            pushToast(t("ui.pinescript.alreadyOnChart"), "info");
            return;
        }
        // 1.5: `overlay=false` cần pane riêng; hết slot thì báo thay vì
        // 2 no-op im lặng (addPane + addSeries đều rơi).
        if (config?.overlay === false) {
            // `PaneState` không có `canAddPane` nên check bằng đúng rule
            // reducer dùng (`useDynamicPanes.ts:366`).
            const visibleCount = paneState.panes.filter((p) => p.visible).length;
            if (visibleCount >= PANE_MAX_VISIBLE) {
                pushToast(t("ui.pinescript.paneLimitReached"), "error");
                return;
            }
            paneState.dispatch({
                type: "addPane",
                pane: { label: "Pine", series: [config], visible: true },
            });
            return;
        }
        paneState.dispatch({
            type: "addSeries",
            paneId: "price",
            series: config,
        });
    }, [paneState, pushToast, t]);
    return (_jsx(BottomPanelHost, { activeTab: activeBottomTab, onTabChange: setActiveBottomTab, plotData: dataResult.plotData, symbol: dataResult.activeSymbol, timeframe: dataResult.activeTimeframe, activeIndicators: activeIndicatorConfigs, onAddIndicator: handleAddIndicator, renderPaperTradePanel: () => (_jsx(Suspense, { fallback: _jsx("div", { "aria-hidden": "true" }), children: _jsx(LazyConnectedTradingPanel, { onClose: () => setActiveBottomTab(null) }) })), panelHeight: bottomPanelHeight, canUsePineEditor: tierGated.canUsePineEditor, canUseStrategyTester: tierGated.canUseStrategyTester, dataAdapter: dataResult.adapter, pineEditorLegacyBars: pineCompileBars }));
}
